import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { createScopedStorage } from "./workspaceStore";
import { getThemeKey } from "../workspace/constants";
import {
  THEME_LIGHT,
  THEME_DARK,
  isDarkTheme,
  isExtraDark,
  isThemeAvailable,
  getAvailableThemes,
  resolveToggleTarget,
} from "../config/themes";

// Move updateDOMThemeStyles to top
function updateDOMThemeStyles(newTheme) {
  const themeLink = document.getElementById("theme-link");
  if (themeLink) {
    // Deploy misconfiguration (theme dir missing on server → CSS 404) must not
    // leave the app silently unstyled (prod incident 2026-07-16). Policy: warn
    // + one-shot degrade to the lara theme of same polarity; no retry loop if
    // the lara CSS itself is what failed.
    themeLink.onerror = () => {
      const fallback = isDarkTheme(newTheme) ? THEME_DARK : THEME_LIGHT;
      const fallbackHref = `/themes/${fallback}/theme.css`;
      if (themeLink.href.endsWith(fallbackHref)) return;
      console.warn(
        `[ThemeStore] Theme CSS "${newTheme}" failed to load — falling back to "${fallback}"`,
      );
      themeLink.href = fallbackHref;
    };
    themeLink.href = `/themes/${newTheme}/theme.css`;
  }

  // Only update body class if body exists (it might not during initial module load)
  if (document.body) {
    if (isDarkTheme(newTheme)) {
      document.body.classList.add("dark-mode-custom");
    } else {
      document.body.classList.remove("dark-mode-custom");
    }
    // Extra Dark navy = the shared dark chrome (dark-mode-custom) + this marker,
    // which flips the --tz-* token map to navy (src/styles/theme-tokens.css).
    if (isExtraDark(newTheme)) {
      document.body.classList.add("theme-extra-dark");
    } else {
      document.body.classList.remove("theme-extra-dark");
    }
  }

  // Safari/Chrome mobile tingem a própria UI (barra de endereço/abas) com o
  // <meta name="theme-color"> — segue o canvas do tema ativo (navy no Extra
  // Dark, slate no dark, branco no light) em vez do ciano fixo da marca.
  // (guardado como o resto da função: o document dos testes é um mock mínimo)
  if (typeof document.querySelector === "function") {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.content = getThemeColors(newTheme).background;
    }
  }

  // color-scheme sinaliza a polaridade da página p/ a UI do próprio browser
  // (Safari 26 usa p/ escurecer a barra de endereço/material glass); o fundo do
  // <html> pinta as safe-areas (status bar / notch) que o viewport-fit=cover
  // revela — no iOS Safari o theme-color NÃO cobre a status bar de forma
  // confiável, então o fundo do html é o mecanismo que realmente funciona.
  if (document.documentElement && document.documentElement.style) {
    document.documentElement.style.colorScheme = isDarkTheme(newTheme)
      ? "dark"
      : "light";
    document.documentElement.style.background =
      getThemeColors(newTheme).background;
  }
}

export const useThemeStore = create(
  persist(
    (set, get) => ({
      theme: THEME_DARK,
      crosshairEnabled: true,

      toggleCrosshair: () =>
        set((state) => ({ crosshairEnabled: !state.crosshairEnabled })),
      // explicit setter so other modules can force crosshair on/off
      setCrosshairEnabled: (enabled) => set({ crosshairEnabled: !!enabled }),

      setTheme: (newTheme) => {
        set({ theme: newTheme });
        updateDOMThemeStyles(newTheme);
      },

      toggleTheme: () => {
        const target = resolveToggleTarget(get().theme);
        if (target !== get().theme) get().setTheme(target);
      },

      // Persisted theme may not exist on this deploy (list shrank via
      // runtime-config, or another deploy wrote the scoped key). Policy (I2):
      // degrade to the available theme of same isDark — never a 404'd CSS.
      // The fallback is persisted (accepted: a shrunk deploy overwrites the
      // exotic choice).
      ensureValidTheme: () => {
        const current = get().theme;
        if (isThemeAvailable(current)) return current;
        const available = getAvailableThemes();
        const fallback =
          available.find((t) => t.isDark === isDarkTheme(current)) ??
          available[0];
        console.warn(
          `[ThemeStore] Theme "${current}" not available on this deploy — falling back to "${fallback.name}"`,
        );
        get().setTheme(fallback.name);
        return fallback.name;
      },

      // Initialize theme on app load (and after workspace-context rehydrate)
      initializeTheme: () => {
        const theme = get().ensureValidTheme();
        updateDOMThemeStyles(theme);
      },
    }),
    {
      name: "theme", // Used as fallback name, actual key is dynamic (user-scoped)
      storage: createJSONStorage(() => createScopedStorage(getThemeKey)),
      // persist.rehydrate() NEVER rejects — zustand routes hydration errors
      // (e.g. corrupt JSON in the scoped key) here. Skip-one policy (I2): keep
      // the current theme (still valid) and surface the failure; the
      // initializeTheme chained after rehydrate re-validates and applies DOM.
      onRehydrateStorage: () => (_state, error) => {
        if (error) {
          console.warn(
            "[ThemeStore] Rehydrate failed — keeping current theme",
            error,
          );
        }
      },
    },
  ),
);

// Listen for workspace context changes and rehydrate the store
if (typeof window !== "undefined") {
  window.addEventListener("workspace-context-changed", () => {
    console.log("[ThemeStore] Rehydrating after workspace context change");
    // Hydration errors are handled inside zustand via onRehydrateStorage
    // (rehydrate() never rejects); this chain only re-validates + applies DOM.
    Promise.resolve(useThemeStore.persist.rehydrate()).then(() =>
      useThemeStore.getState().initializeTheme(),
    );
  });
}

// Base chart-canvas palette by polarity. `isDark` is part of the returned object
// so consumers test THAT, never `background === "#1a1a1a"` (which breaks the
// moment a dark theme picks a different background, e.g. Extra Dark navy).
const buildBasePalette = (dark) => ({
  basicColor: dark ? "#999999" : "#000000",
  wickStroke: dark ? "#687177" : "#000000",
  labelFill: dark ? "#fafa00" : "#79a5c8",
  textFill: dark ? "#cccccc" : "#000000",
  text: dark ? "#ffffff" : "#333333",
  textSecondary: dark ? "#cccccc" : "#666666",
  crosshair: dark ? "#555555" : "#999999",
  mouseCoordinateFill: dark ? "#1f2937" : "#6366f1",
  mouseCoordinateText: "#ffffff",
  gridColor: dark ? "#303045" : "#e0e0f0",
  background: dark ? "#1a1a1a" : "#ffffff",
  border: dark ? "#444444" : "#dddddd",
  // In-chart tooltip/overlay backgrounds (OHLC/indicator value tooltips, price-change
  // & countdown badges, measurement overlay). Per-theme so Extra Dark goes navy
  // instead of staying slate. tooltipBg = 0.75 opacity, overlayBg = 0.95.
  tooltipBg: dark ? "rgba(31, 41, 55, 0.75)" : "rgba(252, 252, 252, 0.75)",
  overlayBg: dark ? "rgba(31, 41, 55, 0.95)" : "rgba(252, 252, 252, 0.95)",
  bbStroke: {
    top: "#964B00",
    middle: "#000000",
    bottom: "#964B00",
  },
  bbFill: "#4682B4",
  isDark: dark,
});

// "Extra Dark" (odin-dark) — navy-black canvas. Base = dark; only these keys
// diverge. Curated from the Samsung force-dark research (surfaces have HIGH
// confidence; the neutrals below are derived to stay coherent with the navy
// family). Source: docs/research/themes/samsung-internet/samsung-navy.tokens.css.
const EXTRA_DARK_OVERRIDES = {
  background: "#000010", // research: dominant canvas navy-black (63% da tela)
  gridColor: "#1a2438", // navy grid (surface-border family, lightened for visibility)
  border: "#16202e", // research: --surface-border
  text: "#e8eaef", // research: primary text (force-dark preserves light text)
  textSecondary: "#999ca1", // research: secondary text
  textFill: "#cbd0da", // derived: cool light (was #cccccc)
  basicColor: "#8b93a3", // derived: navy-neutral (was #999999)
  wickStroke: "#5c6b82", // derived: navy wick (was #687177)
  crosshair: "#3a4258", // derived: navy crosshair (was #555555)
  mouseCoordinateFill: "#102058", // research: --highlight-bg (selection navy)
  tooltipBg: "rgba(9, 14, 25, 0.75)", // navy #090e19 (was slate #1f2937)
  overlayBg: "rgba(9, 14, 25, 0.95)", // navy #090e19
};

// Per-theme chart-canvas palette. lara-dark/light + odin-light stay on the
// polarity base (byte-identical to before + the new isDark field); odin-dark
// layers the navy overrides. Returns a fresh object per call (unchanged churn).
export const getThemeColors = (theme) => {
  const base = buildBasePalette(isDarkTheme(theme));
  return theme === "odin-dark" ? { ...base, ...EXTRA_DARK_OVERRIDES } : base;
};
